import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { useActiveProjectId, useNotify } from '../../hooks';
import { useApiClient } from '../../hooks/useRestAPIHooks';
import { isReviewableResourceType } from '../../util/extractedResourceReview';
import type { MemberExtractionReviewDecision } from './useMemberExtractionDocuments';

const messages = defineMessages({
  approve: {
    id: 'phc-web-toolkit.memberExtractions.review.approve',
    defaultMessage: 'Approve',
  },
  reject: {
    id: 'phc-web-toolkit.memberExtractions.review.reject',
    defaultMessage: 'Reject',
  },
  approveSuccess: {
    id: 'phc-web-toolkit.memberExtractions.review.approveSuccess',
    defaultMessage: 'Extraction approved.',
  },
  rejectSuccess: {
    id: 'phc-web-toolkit.memberExtractions.review.rejectSuccess',
    defaultMessage: 'Extraction rejected and removed.',
  },
  error: {
    id: 'phc-web-toolkit.memberExtractions.review.error',
    defaultMessage: 'Unable to save extraction feedback. Please try again.',
  },
});

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'center',
  gap: 8,
  marginLeft: 'auto',
  flex: '0 0 auto',
};

const buttonClassName = 'member-extraction-review-button';
const approveClassName = 'member-extraction-review-approve';
const rejectClassName = 'member-extraction-review-reject';

const buttonCss = [
  `.${buttonClassName}.${buttonClassName}{min-height:28px;height:auto;min-width:0;padding:2px 10px;font-size:0.8125rem;line-height:1.4;background-color:#fff;transition:background-color 120ms ease,border-color 120ms ease,color 120ms ease,box-shadow 120ms ease;}`,
  `.${approveClassName}.${approveClassName}:hover:not(:disabled){background-color:#e3f2fd;border-color:#1565c0;color:#0d47a1;box-shadow:0 1px 2px rgba(0,0,0,0.12);}`,
  `.${rejectClassName}.${rejectClassName}:hover:not(:disabled){background-color:#fdecea;border-color:#c62828;color:#b71c1c;box-shadow:0 1px 2px rgba(0,0,0,0.12);}`,
  `.${buttonClassName}.${buttonClassName}:active:not(:disabled){box-shadow:none;}`,
].join('');

type MemberExtractionResultReviewProps = {
  patientId: string;
  resourceType: string;
  resourceId: string;
  reviewDecision?: MemberExtractionReviewDecision;
};

export const MemberExtractionResultReview: React.FC<
  MemberExtractionResultReviewProps
> = ({ patientId, resourceType, resourceId, reviewDecision }) => {
  const { formatMessage } = useIntl();
  const projectId = useActiveProjectId();
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const [decided, setDecided] = React.useState(false);
  const [decisionInFlight, setDecisionInFlight] =
    React.useState<MemberExtractionReviewDecision>();

  if (reviewDecision || decided || !isReviewableResourceType(resourceType)) {
    return null;
  }

  const submit = (decision: MemberExtractionReviewDecision) => {
    if (!projectId || decisionInFlight) {
      return;
    }
    setDecisionInFlight(decision);
    apiClient
      .request(
        'POST /v1/agents-v2/document-extraction-v2/resources/:resource_type/:resource_id/feedback',
        {
          resource_type: resourceType,
          resource_id: resourceId,
          subject_id: patientId,
          project_id: projectId,
          decision,
        },
      )
      .then(() => {
        setDecided(true);
        notify(
          formatMessage(
            decision === 'approved'
              ? messages.approveSuccess
              : messages.rejectSuccess,
          ),
          'success',
        );
      })
      .catch(() => {
        notify(formatMessage(messages.error), 'error');
      })
      .finally(() => {
        setDecisionInFlight(undefined);
      });
  };

  return (
    <div style={actionsStyle}>
      <style>{buttonCss}</style>
      <Button
        variant="outlined"
        className={`${buttonClassName} ${approveClassName}`}
        disabled={!!decisionInFlight}
        onClick={() => submit('approved')}
      >
        {formatMessage(messages.approve)}
      </Button>
      <Button
        variant="outlined"
        color="negative"
        className={`${buttonClassName} ${rejectClassName}`}
        disabled={!!decisionInFlight}
        onClick={() => submit('rejected')}
      >
        {formatMessage(messages.reject)}
      </Button>
    </div>
  );
};
